import { useSelector, shallowEqual, useDispatch } from "react-redux";
import { bindActionCreators } from "redux";
import { useCallback, useMemo } from "react";
import SmartDPSearchForm from "app/pages/SmartDP/Search/SDPSearchForm/SmartDPSearchForm";
import PropTypes from "prop-types";
import { getTopDestinations } from "app/reducers/shopSelector";
import {
	QUOTATION_CODE_STATUS,
	RESOLUTION,
	SDP_PRODUCTS_FILTERS_KEY,
	WEBSITE_TUNNEL_TYPES,
} from "app/constants";
import {
	resetAllSDPProductsFilter,
	unsetSDPProductsFilter,
} from "app/pages/SmartDP/Listing/SmartDPFilters/smartDPProductsFilterActionCreators";
import { useLocation, useNavigate } from "react-router-dom";
import {
	buildSDPSearchPayload,
	getLastSearchData,
	getTravelLocationDataFromQuery,
	hasCustomBookingOccupancies,
	normalizeDate,
	pickInitialValue,
} from "app/utils/smartDPUtils";
import withRouter from "app/utils/hocs/withRouter";
import { useLoadingBar } from "app/features/loadingBar";
import { parse, stringify } from "qs";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";

const SmartDPSearchFormContainer = ownProps => {
	const stateProps = useSelector(state => mapStateToProps(state, ownProps), shallowEqual);
	const dispatch = useDispatch();
	const dispatchProps = useMemo(() => mapDispatchToProps(dispatch), [dispatch]);
	const props = { ...ownProps, ...stateProps, ...dispatchProps };

	const { unsetSDPProductsFilter, onSubmit, skipListingRedirect } = props;

	const { pathname, search } = useLocation();

	const isListingPage = pathname.includes("/listing");

	const navigate = useNavigate();
	const { pulse } = useLoadingBar();

	const handleFormSubmission = useCallback(
		({ values, pathname = "" }) => {
			let newQueryParams = buildSDPSearchPayload(values);
			pulse();
			unsetSDPProductsFilter(SDP_PRODUCTS_FILTERS_KEY.BUDGET);

			if (onSubmit) {
				onSubmit(values);
			}

			if (skipListingRedirect) {
				return;
			}

			if (pathname.includes("/sdp/listing")) {
				const currentParams = parse(search, { ignoreQueryPrefix: true });
				const newParams = parse(newQueryParams, { ignoreQueryPrefix: true });

				// After buildSDPSearchPayload produces the new query string, compare the new destination
				// with the current URL:
				// - If the current URL has accommodationId, the new query does NOT already have one, and destinationResortCode
				// is unchanged → preserve the existing accommodationId.
				// - Otherwise (resort changed, or new hotel selected) → let the new query string through as-is.
				if (
					currentParams.accommodationId &&
					!newParams.accommodationId &&
					newParams.destinationResortCode === currentParams.destinationResortCode
				) {
					newQueryParams = stringify(
						{ ...newParams, accommodationId: currentParams.accommodationId },
						{ encodeValuesOnly: true, arrayFormat: "indices" }
					);
				}

				navigate({ pathname, search: newQueryParams }, { replace: true });
			} else {
				navigate({ pathname: "/sdp/listing", search: newQueryParams });
			}
		},
		[search, skipListingRedirect, onSubmit, unsetSDPProductsFilter, pulse]
	);

	return (
		<div className="search-form__container">
			<SmartDPSearchForm
				{...props}
				onSuccess={handleFormSubmission}
				usePortalForMenu={isListingPage}
			/>
		</div>
	);
};

SmartDPSearchFormContainer.propTypes = {
	unsetSDPProductsFilter: PropTypes.func,
	isMobile: PropTypes.bool,
	brand: PropTypes.string,
	disableFormPersistance: PropTypes.bool, // utilisé pour éviter le flicker des champs entre la page merch et la home SDP
	onSubmit: PropTypes.func,
	/** Skips the built-in navigate-to-listing behavior — the caller's `onSubmit` handles everything after submit. */
	skipListingRedirect: PropTypes.bool,
	/**
	 * Unlike disableFormPersistance, still reads the destination/departure/dates/occupancies/cabin
	 * from booking — only disables falling back to the last search when booking has no value.
	 * Used by contexts (ex: hotel page QuickSearch) that explicitly reset booking's fields and don't
	 * want an unrelated previous search to leak back in.
	 */
	skipLastSearchFallback: PropTypes.bool,
};

const mapStateToProps = (state, props) => {
	const queryParams = parse(props?.location?.search, { ignoreQueryPrefix: true });
	const { booking, smartDP } = state;
	const { disableFormPersistance, skipLastSearchFallback } = props;
	const isMerchPage = props?.location?.pathname?.includes("/merch");
	const lastSearch = getLastSearchData(state, WEBSITE_TUNNEL_TYPES.HOLIDAYS);

	const initialValues = {
		travelDates: {
			departureDate: "",
			endDate: "",
		},
	};

	if (smartDP?.cabins?.[0]) {
		initialValues.cabin = smartDP.cabins[0];
	}

	const getLocationsFrom = query =>
		getTravelLocationDataFromQuery({
			queryParams: query,
			destinations: smartDP?.destinations,
			departures: smartDP?.departureCities,
		});

	const { departure: queryDeparture, destination: queryDestination } = getLocationsFrom({
		departureCityCode: queryParams?.departureCityCode,
		destinationResortCode: queryParams?.destinationResortCode,
		destinationIATA: queryParams?.destinationIATA,
	});

	const { departure: bookingDeparture, destination: bookingDestinationFromList } =
		getLocationsFrom({
			departureCityCode: booking?.departureCity?.code,
			destinationResortCode: booking?.destinationResort?.code,
		});

	const bookingDestination = booking?.destinationResort?.isHotel
		? booking.destinationResort
		: bookingDestinationFromList;

	const { departure: lastDeparture, destination: lastDestinationFromList } = getLocationsFrom({
		departureCityCode: lastSearch?.departureCity?.code,
		destinationResortCode: lastSearch?.destinationResort?.code,
	});

	const lastDestination = lastSearch?.searchedAccommodation
		? {
				code: lastSearch.searchedAccommodation.id,
				label: lastSearch.searchedAccommodation.name,
				isHotel: true,
				places: lastSearch.searchedAccommodation.places,
			}
		: lastDestinationFromList;

	// En merch, on ne va pas set  destination/dates mais on peut garder les autres champs
	const canUseLastSearchForMainFields = !disableFormPersistance && !isMerchPage;
	const canUseLastSearchForOtherFields = !disableFormPersistance;

	//  un helper pour ne pas répéter disableFormPersistance et garder la priorité booking -> requête -> dernière recherche.
	const pickValue = params =>
		pickInitialValue({
			disableFormPersistance,
			skipLastSearchFallback,
			...params,
		});

	const normalizeValue = date => (date ? normalizeDate(date) : undefined);

	// 1. On privilégie la destination persistée, puis la query, puis la dernière recherche.
	const destinationResort = pickValue({
		bookingValue: bookingDestination,
		queryValue: queryDestination,
		lastSearchValue: lastDestination,
		allowLastSearch: canUseLastSearchForMainFields,
		isValueValid: value => !isEmpty(value),
	});

	const hasDestination = !isEmpty(destinationResort);

	if (hasDestination) {
		initialValues.destinationResort = destinationResort;

		const departureCity = pickValue({
			bookingValue: bookingDeparture,
			queryValue: queryDeparture,
			lastSearchValue: lastDeparture,
			allowLastSearch: canUseLastSearchForOtherFields,
			isValueValid: value => !isEmpty(value),
		});

		if (departureCity) {
			initialValues.departureCity = departureCity;
		}
	}

	// 2. Dates et durée ne sont reprises que si la destination est connue et la persistance active.
	if (hasDestination && !disableFormPersistance) {
		const departureDate = pickValue({
			bookingValue: normalizeValue(booking?.departureDate),
			lastSearchValue: normalizeValue(lastSearch?.departureDate),
			allowLastSearch: canUseLastSearchForMainFields,
		});

		if (departureDate) {
			initialValues.travelDates.departureDate = departureDate;
		}

		const endDate = pickValue({
			bookingValue: normalizeValue(booking?.endDate),
			lastSearchValue: normalizeValue(lastSearch?.endDate),
			allowLastSearch: canUseLastSearchForMainFields,
		});

		if (endDate) {
			initialValues.travelDates.endDate = endDate;
		}

		const bookingDuration = booking?.duration?.value;
		const lastDuration = lastSearch?.duration?.value;

		const durationCode = pickValue({
			bookingValue: bookingDuration,
			lastSearchValue: lastDuration,
			allowLastSearch: canUseLastSearchForMainFields,
			isValueValid: value => !isNil(value),
		});

		if (!isNil(durationCode)) {
			initialValues.duration = { code: durationCode };
		}
	}

	// 3. Occupancies et cabine ne sont relus que lorsque l'utilisateur a réellement modifié ces champs.
	if (!disableFormPersistance) {
		const bookingOccupancies = hasCustomBookingOccupancies(booking)
			? booking?.occupancies
			: undefined;
		const lastSearchOccupancies =
			lastSearch?.occupancies?.length > 0 ? lastSearch?.occupancies : undefined;

		const occupancies = pickValue({
			bookingValue: bookingOccupancies,
			lastSearchValue: lastSearchOccupancies,
			allowLastSearch: canUseLastSearchForOtherFields,
			isValueValid: value => Array.isArray(value) && value.length > 0,
		});

		if (occupancies) {
			initialValues.occupancies = occupancies;
		}

		const bookingCabin =
			!isEmpty(booking?.cabin) && Boolean(booking?.cabin?.code) ? booking?.cabin : undefined;
		const lastSearchCabin =
			!isEmpty(lastSearch?.cabin) && Boolean(lastSearch?.cabin?.code)
				? lastSearch?.cabin
				: undefined;

		const cabin = pickValue({
			bookingValue: bookingCabin,
			lastSearchValue: lastSearchCabin,
			allowLastSearch: canUseLastSearchForOtherFields,
			isValueValid: value => !isEmpty(value) && Boolean(value?.code),
		});

		if (cabin) {
			initialValues.cabin = cabin;
		}
	}

	return {
		initialValues,
		destinations: state.smartDP.destinations,
		departureCities: state.smartDP.departureCities,
		cabins: state.smartDP.cabins,
		validateOnMount: state.smartDP.status === QUOTATION_CODE_STATUS.BAD_REQUEST,
		topDestinationsList: getTopDestinations(state) || [],
		isMobile: [RESOLUTION.SMALL, RESOLUTION.MEDIUM].includes(state?.resolution),
		brand: state.brand.code,
	};
};

const mapDispatchToProps = dispatch => {
	return bindActionCreators(
		{
			unsetSDPProductsFilter,
			resetAllSDPProductsFilter,
		},
		dispatch
	);
};

export default withRouter(SmartDPSearchFormContainer);
